import React from 'react';
import { Star } from 'lucide-react';
import MathText from './ui/MathText';
import SentenceBuilder from './ui/SentenceBuilder';
import { OPTION_KEYS, TC_BLANK_LABELS, WORD_BANK_TOPICS, PASSAGE_IMAGE_TOPICS } from '../utils/Constants';
import { ClickToViewImage } from './ImageViewer';

// One quiz question card — the single source of truth for how a question renders during a quiz.
// Used by the live quiz (QuizTakingInterface) AND the admin "Preview Question In Quiz" modal, so
// the preview can never drift from the real thing.
//
// Props:
//   question      — { question_id, question_type, question_text, option_a..h, option_*_image_url,
//                     question_image_url, multiple_correct, side_text, audio_url, metadata.blanks }
//   qIndex        — 0-based position (shown as "Q{qIndex+1}.")
//   includePassage— render the question's own side_text/audio inline (standalone questions)
//   answer        — current selection: option key string | array of keys | { text } | { blanks:{i:c} }
//   isFavorite    — star fill state
//   onOptionSelect(qid, optionKey)   — single-select MCQ
//   onMultiToggle(qid, optionKey)    — multi-select MCQ / Sentence Equivalence
//   onBlankSelect(qid, blankIdx, ci) — Text Completion
//   onTextChange(qid, text)          — TEXT free answer
//   onOrderChange(qid, order)        — Build a Sentence (ordered tile indices)
//   onToggleFavorite(qid)            — optional; the star is hidden when not provided (preview)
export default function QuizQuestionCard({
    question,
    qIndex,
    displayNumber,
    textareaRows = 3,
    includePassage,
    answer,
    isAnswered,
    isFavorite,
    onOptionSelect,
    onMultiToggle,
    onBlankSelect,
    onTextChange,
    onOrderChange,
    onToggleFavorite,
}) {
    const isTextQuestion = question.question_type === 'TEXT';
    const isTC = question.question_type === 'TEXT_COMPLETION';
    const isMatching = question.question_type === 'MATCHING';
    const isBuildSentence = question.question_type === 'BUILD_SENTENCE';
    // IELTS note/table/summary/sentence completion is a short-answer "fill in the blank" (a numbered
    // metadata.ielts.no), so it gets a single-line input rather than an essay textarea — the closest
    // presentation to the model test's inline blanks when there is no blueprint template (daily quiz).
    const ielts = question.metadata && question.metadata.ielts;
    const isCompletionBlank = isTextQuestion && ielts && ielts.no != null && ielts.no !== '';
    // GRE Quant / Math student-produced responses are short numeric answers — a single-line input
    // (not the big essay textarea). Essay/Writing TEXT questions keep the multi-line textarea.
    const isShortNumericText = isTextQuestion && !isCompletionBlank && /quant|math/i.test(question.subject || '');
    const isMulti = !!question.multiple_correct;
    const tcBlanks = (question.metadata && question.metadata.blanks) || [];
    const matchBox = (question.metadata && question.metadata.match_options) || {};
    const matchImage = question.metadata && question.metadata.match_image;
    // IELTS gap-fill word bank: shared read-only word bank (A→word) shown above the typed blank;
    // the taker enters the matching letter (A–J). IELTS Listening map/plan/diagram: shared image.
    const wordBank = WORD_BANK_TOPICS.includes(question.topic) && question.metadata ? question.metadata.word_bank : null;
    const passageImage = PASSAGE_IMAGE_TOPICS.includes(question.topic) && question.metadata ? question.metadata.passage_image : null;
    return (
        <div style={{ marginBottom: '1rem', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', backgroundColor: '#ffffff', boxShadow: isAnswered ? '0 4px 6px -1px rgba(34, 197, 94, 0.1), 0 2px 4px -2px rgba(34, 197, 94, 0.06)' : '0 4px 6px -1px rgba(234, 179, 8, 0.1), 0 2px 4px -2px rgba(234, 179, 8, 0.06)', position: 'relative' }}>
            {includePassage && question.side_text && (
                <div style={{ background: '#f3f4f6', borderLeft: '3px solid #9ca3af', padding: '0.5rem 0.75rem', borderRadius: '0.25rem', marginBottom: '0.75rem', whiteSpace: 'pre-wrap' }} className="kp-read-passage"><MathText text={question.side_text} /></div>
            )}
            {includePassage && question.audio_url && (
                <audio controls src={question.audio_url} style={{ width: '100%', marginBottom: '0.75rem' }} />
            )}
            {passageImage && (
                <div style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
                    <ClickToViewImage src={passageImage} imgStyle={{ maxWidth: '100%', margin: '0 auto' }} />
                </div>
            )}
            {wordBank && Object.keys(wordBank).length > 0 && (
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '0.5rem', padding: '0.6rem 0.9rem', marginBottom: '0.75rem' }}>
                    <div className="d-flex flex-wrap" style={{ gap: '0.35rem 1.5rem' }}>
                        {Object.keys(wordBank).map((l) => (
                            <span key={l} style={{ color: '#4c1d95' }}>
                                <strong>{l}.</strong>{' '}<MathText text={String(wordBank[l])} />
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                    {question.question_image_url && (
                        <ClickToViewImage src={question.question_image_url} />
                    )}
                    <p className="kp-read-question" style={{ margin: 0 }}>
                        {/* displayNumber (canonical paper number, e.g. "11" or "29 & 30") overrides the
                            positional index; an explicit empty string suppresses the label (Writing).
                            The label reads "Question N" in bold orange; the question text itself is plain. */}
                        {displayNumber === '' ? null : (
                            <span style={{ fontWeight: 700, color: '#f97316', marginRight: 6 }}>
                                {displayNumber != null
                                    ? String(displayNumber).split(/\s*&\s*/).map((n) => `Question ${n.trim()}`).join(' & ')
                                    : `Question ${qIndex + 1}`}
                            </span>
                        )}
                        <MathText text={question.question_text} />
                        {isMulti && <span style={{ fontWeight: 500, color: '#6b7280', fontSize: '0.8rem' }}> (select all that apply)</span>}
                    </p>
                </div>
                {onToggleFavorite && (
                    <button onClick={() => onToggleFavorite(question.question_id)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: isFavorite ? '#fbbf24' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Star style={{ width: '1.25rem', height: '1.25rem' }} fill={isFavorite ? '#fbbf24' : 'none'} />
                    </button>
                )}
            </div>
            {(isCompletionBlank || isShortNumericText) ? (
                <input
                    type="text"
                    className="form-control"
                    placeholder="Type your answer…"
                    value={(answer && answer.text) || ''}
                    onChange={e => onTextChange && onTextChange(question.question_id, e.target.value)}
                    style={{ maxWidth: '18rem', borderColor: '#c4b5fd' }}
                />
            ) : isTextQuestion ? (
                <textarea
                    className="form-control"
                    rows={textareaRows}
                    placeholder="Type your answer…"
                    value={(answer && answer.text) || ''}
                    onChange={e => onTextChange && onTextChange(question.question_id, e.target.value)}
                />
            ) : isBuildSentence ? (
                <SentenceBuilder
                    question={question}
                    answer={answer}
                    onOrder={(qid, order) => onOrderChange && onOrderChange(qid, order)}
                />
            ) : isMatching ? (
                <div>
                    {matchImage && (
                        <ClickToViewImage src={matchImage} containerStyle={{ textAlign: 'center' }} imgStyle={{ margin: '0 auto' }} />
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {Object.keys(matchBox).map((letter) => {
                            const selected = answer === letter;
                            return (
                                <button
                                    key={letter}
                                    type="button"
                                    onClick={() => onOptionSelect && onOptionSelect(question.question_id, letter)}
                                    className={`quiz-option${selected ? ' selected' : ''}`}
                                    style={{ flex: '1 1 200px' }}
                                >
                                    <span style={{ fontWeight: 600, marginRight: '0.5rem' }}>({letter})</span>
                                    <MathText text={String(matchBox[letter])} />
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : isTC ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'stretch' }}>
                    {tcBlanks.map((blank, bi) => (
                        <div key={bi} style={{ flex: '1 1 200px', minWidth: '180px', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem', backgroundColor: '#f9fafb' }}>
                            <div style={{ fontWeight: 700, color: '#4338ca', marginBottom: '0.5rem', textAlign: 'center', borderBottom: '2px solid #e0e7ff', paddingBottom: '0.35rem' }}>Blank {TC_BLANK_LABELS[bi] || `#${bi + 1}`}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                {(blank.choices || []).map((choice, ci) => {
                                    const selected = answer && answer.blanks && answer.blanks[bi] === ci;
                                    return (
                                        <button
                                            key={ci}
                                            type="button"
                                            onClick={() => onBlankSelect && onBlankSelect(question.question_id, bi, ci)}
                                            className={`quiz-option${selected ? ' selected' : ''}`}
                                        >
                                            <span style={{ fontWeight: 600, marginRight: '0.5rem' }}>({String.fromCharCode(65 + ci)})</span>
                                            <MathText text={choice} />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {OPTION_KEYS.map(optionKey => {
                        if (!question[optionKey]) return null;
                        const selected = isMulti
                            ? Array.isArray(answer) && answer.includes(optionKey)
                            : answer === optionKey;
                        const imgKey = `${optionKey}_image_url`;
                        const optionLetter = optionKey.slice(-1).toUpperCase();
                        return (
                            <button
                                key={optionKey}
                                type="button"
                                onClick={() => isMulti
                                    ? (onMultiToggle && onMultiToggle(question.question_id, optionKey))
                                    : (onOptionSelect && onOptionSelect(question.question_id, optionKey))}
                                className={`quiz-option${selected ? ' selected' : ''}`}
                            >
                                {isMulti && <span style={{ marginRight: '0.5rem' }}>{selected ? '☑' : '☐'}</span>}
                                <span style={{ fontWeight: 600, marginRight: '0.5rem' }}>({optionLetter})</span>
                                {question[imgKey] && (
                                    <ClickToViewImage src={question[imgKey]} imgStyle={{ height: 170 }} />
                                )}
                                <MathText text={question[optionKey]} />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
